import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {Uuid} from '../interface/response/uuid';
import {first, map, take} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {OfwcaRegistrationInfo} from '../interface/ofwca-registration-info';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {Registration} from '../interface/registration';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {ListResponse} from '../interface/response/list-response';
import {Document as DocumentInterface} from '../interface/document';
import {RegistrationIdentifier} from '../interface/registration-identifier';
import {SalesProfile} from '../interface/sales-profile';
import {Contract} from '../interface/contract';
import {ReportCustom} from "../interface/report-custom";

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private readonly registrationListSubject: ReplaySubject<CountedAndPaginated<Registration>>
    = new ReplaySubject<CountedAndPaginated<Registration>>(1);
  private readonly registrationShortListSubject: ReplaySubject<CountedAndPaginated<Registration>>
    = new ReplaySubject<CountedAndPaginated<Registration>>(1);
  private readonly registrationNotCountedListSubject: ReplaySubject<ListResponse<Registration>>
    = new ReplaySubject<ListResponse<Registration>>(1);
  public readonly salesProfileListSubject: ReplaySubject<CountedAndPaginated<SalesProfile>>
    = new ReplaySubject<CountedAndPaginated<SalesProfile>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  sendForm(formData: any, registrationId?: string): Observable<Uuid> {
    if (!registrationId) {
      return this.httpClient.post(`/rest/crm/registration`, formData)
        .pipe(take(1)) as Observable<Uuid>;
    } else {
      return this.httpClient.put(`/rest/crm/registration/${registrationId}`, formData)
        .pipe(take(1)) as Observable<Uuid>;
    }
  }
  sendMultipleForm(formData: any):  Observable<any> {
      return this.httpClient.post(`/rest/crm/registration/multiple`, formData)
        .pipe(first());
  }
  getOfwcaRegistrationInfo(ofwcaId: string): Observable<OfwcaRegistrationInfo> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/registrationInfo`)
      .pipe(first()) as Observable<OfwcaRegistrationInfo>;
  }

  get(registrationId: string): Observable<Registration> {
    return this.httpClient.get(`/rest/crm/registration/${registrationId}`)
      .pipe(first()) as Observable<Registration>;
  }

  public loadList(page: number, pageSize: number, filters: object, sort?: {sortBy?: string; sortOrder?: string}): void
  {
    let loadFilter: string;
    if (sort !== null){
      loadFilter = `sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}&` + this.filterService.parseFilters(filters);
    } else {
      loadFilter = this.filterService.parseFilters(filters);
    }
    this.httpClient.get(`/rest/crm/registration?page=${page + 1}&pageSize=${pageSize}&` + loadFilter)
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Registration>) => {
        this.registrationListSubject.next(result);
      });
  }

  public loadShortList(page: number, pageSize: number, filters: object, sort?: {sortBy?: string; sortOrder?: string}): void
  {
    let loadFilter: string;
    if (sort !== null){
      loadFilter = `sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}&` + this.filterService.parseFilters(filters);
    } else {
      loadFilter = this.filterService.parseFilters(filters);
    }
    this.httpClient.get(`/rest/crm/registration-short?page=${page + 1}&pageSize=${pageSize}&` + loadFilter)
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Registration>) => {
        this.registrationShortListSubject.next(result);
      });
  }

  public loadSalesProfileList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(
      `/rest/crm/registration-sales-profile?page=${page + 1}&pageSize=${pageSize}&`
        + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<SalesProfile>) => {
        this.salesProfileListSubject.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<Registration>>): void {
    this.httpClient
      .get(`/rest/crm/registration?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Registration>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.registrationNotCountedListSubject.next(result);
        }
      });
  }

  getRegistrationListSubject(): ReplaySubject<CountedAndPaginated<Registration>> {
    return this.registrationListSubject;
  }

  getRegistrationShortListSubject(): ReplaySubject<CountedAndPaginated<Registration>> {
    return this.registrationShortListSubject;
  }

  getRegistrationNotCountedListSubject(): ReplaySubject<ListResponse<Registration>> {
    return this.registrationNotCountedListSubject;
  }

  downloadReport(filters: any, reportConsents = 0,  sort?: { sortBy?: string, sortOrder?: string}): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/method/report?reportConsents=${reportConsents}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  downloadReportExamStatus(filters: any, reportConsents = 0,  sort?: { sortBy?: string, sortOrder?: string}): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/method/examStatus/report?reportConsents=${reportConsents}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }


  downloadReportToBeBlocked(filters: any, reportConsents = 0): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/method/toBeBlocked/report?reportConsents=${reportConsents}&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  downloadChronieReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/method/chronieReport?reportConsents=0&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  downloadPzuFormReport(filters: any, reportConsents = 0,  sort?: { sortBy?: string, sortOrder?: string}): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/method/pzuFormReport?reportConsents=${reportConsents}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  downloadVerificationReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/verification/method/report?&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  deleteRegistration(registrationId: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/registration/${registrationId}`)
      .pipe(first());
  }

  getRegistrationDocuments(registrationId: string): Observable<ListResponse<DocumentInterface>> {
    return this.httpClient.get(`/rest/crm/registration/${registrationId}/document`)
      .pipe(first()) as Observable<ListResponse<DocumentInterface>>;
  }

  public getRegistrationIds(registrationId: string): Observable<ListResponse<RegistrationIdentifier>> {
    return this.httpClient.get(`/rest/crm/registration/${registrationId}/registration-id`)
      .pipe(first()) as Observable<ListResponse<RegistrationIdentifier>>;
  }

  public cancelRegistration(formData: any): Observable<any> {
    return this.httpClient.post(`/rest/crm/registration/method/cancel`, formData ).pipe(first());
  }

  downloadSalesProfileReport(filters: any): void {
    this.document.location.href = `/rest/crm/registration-sales-profile/method/report?` + this.filterService.parseFilters(filters);
  }

  updateSalesProfile(uuid: string, form: { note: any; status: number }): Observable<any> {
    return this.httpClient.put(`/rest/crm/registration-sales-profile/${uuid}`, form)
      .pipe(first());
  }

  deleteSalesProfile(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/registration-sales-profile/${uuid}`)
      .pipe(first());
  }

  canRegisterToExam(uuid: string): Observable<boolean> {
    return this.httpClient.get(`/rest/crm/registration/${uuid}/canRegisterToExam`)
      .pipe(first(), map((result: any) => result.result));
  }

  updateAgreedToDeregister(uuid: string, formData: {agreedToDeregister: boolean}): Observable<any> {
    return this.httpClient.put(`/rest/crm/registration/${uuid}/updateAgreedToDeregister`, formData)
      .pipe(first());
  }

  updateBlockedRegistration(uuid: string, formData: { blocked: boolean; sentToBlock: boolean }): Observable<any> {
    return this.httpClient.patch(`/rest/crm/registration/${uuid}/updateBlocked`, formData)
      .pipe(first()) as Observable<any>;
  }

  updatePzuRegistration(uuid: string, formData:
    {sentToPzuForVerification: boolean;
      pzuVerificationSupplement: boolean;
      agreedToRegisterInPzu: boolean}): Observable<any> {
    return this.httpClient.put(`/rest/crm/registration/${uuid}/updatePzu`, formData)
      .pipe(first());
  }

  getOfwcaRegistrationContracts(ofwcaId: string): Observable<ListResponse<Contract>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/registration-contracts`)
      .pipe(first()) as Observable<ListResponse<Contract>>;
  }
  public downloadCustomReport(filters: object, sort?: {
    sortBy?: string,
    sortOrder?: string
  }): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/custom/method/report?&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  public getCustomReportSettings(reportName: string): Observable<ReportCustom> {
    return this.httpClient.get(`/rest/crm/report/custom/settings/${reportName}`)
      .pipe(first()) as Observable<ReportCustom>
      ;
  }
  public sendCustomReportSettings(formData: object): Observable<any> {
    return this.httpClient.post('/rest/crm/report/custom/settings', formData)
      .pipe(take(1));
  }

  downloadDoubleRegistrationReport(filters: any,  sort?: { sortBy?: string, sortOrder?: string}): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/method/report/double?&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  downloadPzuRegistrationReport(filters: any,  sort?: { sortBy?: string, sortOrder?: string}): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/pzu/method/report?&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public blockRegistration(registrationId: string, reason: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/registration/method/block`, {
      registration: registrationId,
      reason
    }).pipe(first());
  }

  public getRegistrationIdsSupport(registrationId: string): Observable<ListResponse<RegistrationIdentifier>> {
    return this.httpClient.get(`/rest/crm/registration/${registrationId}/registration-id-support`)
      .pipe(first()) as Observable<ListResponse<RegistrationIdentifier>>;
  }
}
